import { Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Container from "../../../../components/Container";
import { Badge, Button, CircularProgress, Divider, Grid2, IconButton, Skeleton, TextField, Typography } from "@mui/material";
import { ArrowBackIos, ArrowForwardIos, AssignmentReturn, AssignmentReturned, Check, Close, Warning } from "@mui/icons-material";
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import api from "../../../../utils/axios";
import { IOrder, IOrderSocketResponse, TStepStatus } from "sierra";
import OrderStepper from "../../../../components/OrderStepper";
import { useAuth } from "../../../../hooks/useAuth";
import { useApp } from "../../../../contexts/AppContext";
import ConfirmDialog from "../../../../components/ConfirmDialog";
import socket from "../../../../utils/socket";
import OrderManageSkeleton from "../../../../components/Skeletons/OrderManageSkeleton";

const OrderStepperPanel = ({ order, setStepIndex }: { order: IOrder, setStepIndex?: Dispatch<SetStateAction<number>> }) => {

    return (
        <OrderStepper form={order.order_details} readOnly={true} setStepIndex={setStepIndex} />
    )
}

export default function SystemOrderManage() {
    const { user, userLoaded } = useAuth()
    const { lang } = useApp()
    const location = useLocation()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const { translate } = useApp()

    const [order, setOrder] = useState<IOrder>()
    const [tab, setTab] = useState<'' | 'assignment'>()
    const [stepIndex, setStepIndex] = useState<number>(0)
    const [pendingReason, setPendingReason] = useState<string>()
    const [openPending, setOpenPending] = useState<boolean>(false)
    const [openConfirmComplete, setOpenConfirmComplete] = useState<boolean>(false)
    const [apiCall, setApiCall] = useState<boolean>(false)
    const [cancelOrder, setCancelOrder] = useState<boolean>(false)
    const [cancelling, setCancelling] = useState<boolean>(false)
    const [unreadCount, setUnreadCount] = useState(0)

    const orderRef = useRef(order)

    const order_id = searchParams.get('order_id');

    useEffect(() => {
        if (!user) return
        fetchOrder()
        if (['manager', 'employee'].includes(user.role)) fetchUnreadCount()
        socket.on("orders_changed", handleOrderListener);
        if (['manager', 'employee'].includes(user.role)) socket.on("orders_messages_changed", fetchUnreadCount);
        return () => {
            socket.off("orders_changed", handleOrderListener);
            if (['manager', 'employee'].includes(user.role)) socket.off("orders_messages_changed", fetchUnreadCount);
        };
    }, [user])

    useEffect(() => {
        orderRef.current = order; // Always update ref when order change
    }, [order]);

    useEffect(() => {
        if (tab === 'assignment') navigate('assignment?order_id=' + order_id)
    }, [tab])

    useEffect(() => {
        console.log('location is', location)
        if (tab && !location.pathname.match(tab)) setTab('')
    }, [location, tab])

    const fetchOrder = () => {
        api.get(`/orders`, { params: { order_id, exclude_fields: 'service_form' } }).then(({ data }) => setOrder(data[0])).catch(console.error)
    }

    const handleUpdateStepStatus = useCallback((stepStatus: TStepStatus) => {
        if (!order) return
        setApiCall(true)
        api.patch(`/orders/${order_id}/status/${order.order_details[stepIndex].step_id}`, { step_status: stepStatus, pending_reason: pendingReason || undefined }, { meta: { showSnackbar: true } }).then(() => {
            fetchOrder()
            setOpenPending(false)
            setApiCall(false)
        }).catch(err => {
            console.log(err)
            setApiCall(false)
        })
    }, [order, order_id, stepIndex, pendingReason])

    const handleOrderListener = useCallback((data: IOrderSocketResponse[]) => {
        if (!orderRef.current) return
        const changedOrderIds = data.map(order => order.order_id);
        const shouldFetch = changedOrderIds.includes(orderRef.current.order_id)
        if (shouldFetch) {
            fetchOrder();
        }
    }, [])

    const handleCancelOrder = () => {
        setCancelling(true)
        api.patch(`/orders/${order_id}/cancelOrder`, {}, { meta: { showSnackbar: true } }).catch(console.error).finally(() => setCancelling(false))
    }

    const fetchUnreadCount = (data?: Array<{ message_id: number, order_id: number }>) => {
        console.log('fetchUnreadCount', data)
        if (data && !data.find(d => d.order_id === Number(order_id))) return;
        api.get(`/ordersChat/${order_id}/unread`).then(({ data }) => setUnreadCount(data.count)).catch(console.error)
    }

    if (!order_id) navigate('/system/orders')

    return (
        <Grid2 container size='grow' spacing={location.pathname.split('/')[4] ? 2 : 0} sx={{ transition: { xs: 'none', lg: location.pathname.split('/')[4] ? 'all 0.8s cubic-bezier(0, 0.5, 0.2, 1)' : 'all 0.5s cubic-bezier(0.6, 0, 0.8, 0.4)' } }}>

            <Grid2 container flexDirection={'column'} gap={2} display={{ xs: location.pathname.split('/')[4] ? 'none' : 'flex', lg: 'flex' }} size={{ xs: location.pathname.split('/')[4] ? 0 : 'grow', lg: 'grow' }}>
                <Container size='auto' padding={2}>
                    <Grid2 container alignItems={'center'}>
                        <Grid2>
                            <IconButton onClick={() => navigate('/system/orders')} color="primary" disableRipple>
                                {lang === 'ar' ? <ArrowForwardIos /> : <ArrowBackIos />}
                            </IconButton>
                        </Grid2>
                        <Grid2>
                            <Typography variant="h5" fontWeight={'bold'} color="primary">
                                {translate("Manage Order")} # {order_id}
                            </Typography>
                        </Grid2>
                    </Grid2>
                </Container>

                <Container container size='auto' padding={2} gap={1.5} justifyContent={'space-between'} alignItems={'center'}>
                    {!order || !user ? <Grid2 container gap={2}>{Array.from({ length: 3 }).map((_, i) => <Grid2 key={i} width={{ xs: 75, sm: 150 }}><Skeleton variant="rectangular" width={'100%'} height={40} /></Grid2>)}</Grid2> :
                        <>
                            <Grid2 container gap={1.5}>
                                <Grid2>
                                    <Button variant="outlined" onClick={() => navigate('info?order_id=' + order_id, { state: { order } })} sx={{ fontWeight: 'bold', borderWidth: '2px' }}>{translate("Order Information")}</Button>
                                </Grid2>
                                <Grid2>
                                    <Button variant="outlined" onClick={() => navigate('customer?order_id=' + order_id, { state: { order } })} sx={{ fontWeight: 'bold', borderWidth: '2px' }}>{translate("Customer Profile")}</Button>
                                </Grid2>
                                <Grid2 display={order.is_cancelled ? 'none' : user.role === 'manager' ? 'flex' : 'none'}>
                                    <Button variant="outlined" onClick={() => navigate('assignment?order_id=' + order_id, { state: { order } })} sx={{ fontWeight: 'bold', borderWidth: '2px' }}>{translate("Update Assignment")}</Button>
                                </Grid2>
                                <Grid2 display={['manager', 'employee'].includes(user.role) ? 'flex' : 'none'}>
                                    <Badge badgeContent={unreadCount} color="error">
                                        <Button variant="outlined" onClick={() => navigate('chat?order_id=' + order_id, { state: { order } })} sx={{ fontWeight: 'bold', borderWidth: '2px' }}>{translate("Customer Chat")}</Button>
                                    </Badge>
                                </Grid2>
                            </Grid2>
                            {order.is_cancelled ? <Grid2><Typography fontWeight={'bold'} color="error">{translate("This order has been cancelled")}</Typography></Grid2> :
                                order.order_status === 'completed' ? <Grid2><Typography fontWeight={'bold'} color="success">{translate("This order has been completed")}</Typography></Grid2> :
                                    <Grid2 display={['manager', 'employee'].includes(user.role) ? 'flex' : 'none'}>
                                        <ConfirmDialog
                                            title={translate('Cancel Order')}
                                            text={translate('Are you sure you want to cancel this order? This action cannot be reversed.')}
                                            open={cancelOrder}
                                            setOpen={setCancelOrder}
                                            // component={<BranchCard branch={branch} hideButtons={true} />}
                                            onConfirm={() => {
                                                handleCancelOrder()
                                                setCancelOrder(false)
                                            }}>
                                            <Button variant="outlined" color="error" disabled={cancelling} onClick={() => setCancelOrder(true)} sx={{ fontWeight: 'bold', borderWidth: '2px' }} startIcon={<Warning />}>{order.order_paid ? 'Cancel & Refund Order' : 'Cancel Order'}</Button>
                                        </ConfirmDialog>
                                    </Grid2>}
                        </>
                    }
                </Container>

                <Container container padding={2} size='grow' flexDirection={'column'} spacing={2}>
                    {
                        !order ?
                            <OrderManageSkeleton inSystem />
                            :
                            <>
                                <Grid2 size={'grow'}>
                                    <OrderStepperPanel order={order} setStepIndex={setStepIndex} />
                                </Grid2>
                                {!order.is_cancelled && user?.role === 'employee' && <>
                                    <Divider flexItem sx={{ backgroundColor: t => t.palette.primary.main }} />
                                    {order.order_details[stepIndex].step_status !== 'completed' ?
                                        <Grid2 container display={'flex'} alignItems={'center'} spacing={2}>
                                            {order.order_details[stepIndex].step_status !== 'pending' ?
                                                <>
                                                    <Grid2 display={openPending ? 'none' : 'flex'}>
                                                        <Button variant="outlined" color="error" sx={{ fontWeight: 'bold', borderWidth: '2px' }} onClick={() => setOpenPending(true)} startIcon={<AssignmentReturn />}>{translate("Request Step Update")}</Button>
                                                    </Grid2>
                                                    <Grid2 container display={openPending ? 'flex' : 'none'} spacing={1}>
                                                        <Grid2>
                                                            <TextField
                                                                size="small"
                                                                label={translate('Pending Reason')}
                                                                multiline
                                                                value={pendingReason || ''}
                                                                onChange={e => setPendingReason(e.target.value)}
                                                                sx={{ backgroundColor: 'rgba(255, 255, 255, 0.3)' }}
                                                            />
                                                        </Grid2>
                                                        <Grid2>
                                                            <Button variant="outlined" color="error" sx={{ fontWeight: 'bold', borderWidth: '2px' }} onClick={() => handleUpdateStepStatus('pending')} disabled={apiCall}>{translate("Confirm")}</Button>
                                                        </Grid2>
                                                        <Grid2>
                                                            <IconButton onClick={() => setOpenPending(false)}>
                                                                <Close />
                                                            </IconButton>
                                                        </Grid2>
                                                    </Grid2>
                                                </>
                                                :
                                                <>
                                                    <Grid2>
                                                        <Button variant="outlined" sx={{ fontWeight: 'bold', borderWidth: '2px' }} onClick={() => handleUpdateStepStatus('validating')} disabled={apiCall}>{translate("Cancel Update Request")}</Button>
                                                    </Grid2>
                                                </>
                                            }

                                            <Grid2 container spacing={1} size={'grow'} justifyContent={'center'} wrap="nowrap">
                                                <Grid2>
                                                    <Typography>{translate("Step Status")}:</Typography>
                                                </Grid2>
                                                <Grid2>
                                                    <Typography
                                                        color={
                                                            order.order_details[stepIndex].step_status === 'awaiting' ? 'warning' :
                                                                order.order_details[stepIndex].step_status === 'pending' ? 'error' :
                                                                    order.order_details[stepIndex].step_status === 'validating' ? 'info' : undefined
                                                        }
                                                        textTransform={'capitalize'}
                                                    >{translate(order.order_details[stepIndex].step_status)}</Typography>
                                                </Grid2>
                                                <Grid2>
                                                    <Divider orientation="vertical" sx={{ backgroundColor: t => t.palette.primary.main }} />
                                                </Grid2>
                                                <Grid2>
                                                    <Typography>{translate("Active Step")}:</Typography>
                                                </Grid2>
                                                <Grid2>
                                                    <Typography textTransform={'capitalize'} fontWeight={'bold'} sx={{ color: t => t.palette.primary.main }} >{order.order_details[stepIndex].step_name[lang]}</Typography>
                                                </Grid2>
                                            </Grid2>

                                            {order.order_details[stepIndex].step_status !== 'pending' &&
                                                <>
                                                    <Grid2 container size='auto'>
                                                        <Grid2 display={order.order_details[stepIndex].step_status !== 'awaiting' ? 'flex' : 'none'}>
                                                            <Button variant="outlined" color="warning" sx={{ fontWeight: 'bold', borderWidth: '2px' }} onClick={() => handleUpdateStepStatus('awaiting')} disabled={apiCall} startIcon={<AssignmentReturn sx={{ transform: 'scaleX(-1)' }} />}>{translate("Awaiting External Approval")}</Button>
                                                        </Grid2>
                                                        <Grid2 display={order.order_details[stepIndex].step_status !== 'validating' ? 'flex' : 'none'}>
                                                            <Button variant="outlined" color="info" sx={{ fontWeight: 'bold', borderWidth: '2px' }} onClick={() => handleUpdateStepStatus('validating')} disabled={apiCall} startIcon={<AssignmentReturned />}>{translate("Validating Information")}</Button>
                                                        </Grid2>
                                                    </Grid2>

                                                    <Grid2>
                                                        <ConfirmDialog
                                                            title={`${translate("(Irreversible!) Complete Step")}: ${order.order_details[stepIndex].step_name[lang]}`}
                                                            text={`${translate("Are you sure you want to set this step? you won't be able to update this step again")}!`}
                                                            open={openConfirmComplete}
                                                            setOpen={setOpenConfirmComplete}
                                                            onConfirm={() => {
                                                                setOpenConfirmComplete(false)
                                                                handleUpdateStepStatus('completed')
                                                            }}
                                                        >
                                                            <Button variant="contained" color="success" onClick={() => setOpenConfirmComplete(true)} disabled={apiCall} startIcon={<Check />}>
                                                                {translate("Complete Step")}
                                                            </Button>
                                                        </ConfirmDialog>
                                                    </Grid2>
                                                </>
                                            }
                                            {apiCall && <Grid2><CircularProgress /></Grid2>}

                                        </Grid2>
                                        :
                                        <Grid2>
                                            <Typography color="success">{translate("This step has been COMPLETED and cannot be updated")}</Typography>
                                        </Grid2>
                                    }
                                </>}
                            </>
                    }
                </Container>
            </Grid2>

            <Container size={location.pathname.split('/')[4] ? 'grow' : -1} sx={{ transition: { xs: 'none', lg: 'all 0.5s ease' } }}>
                <Outlet />
            </Container>

        </Grid2>
    )
}